import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Copy, Check, FlaskConical, Store, ExternalLink } from "lucide-react";
import api from "@/lib/api";
import { useT } from "@/i18n";
import { useStore } from "@/context/StoreProvider";

const ASSISTANTS = [
  { key: "chatgpt", label: "ChatGPT", url: "https://chatgpt.com" },
  { key: "gemini", label: "Gemini", url: "https://gemini.google.com" },
  { key: "claude", label: "Claude", url: "https://claude.ai" },
  { key: "perplexity", label: "Perplexity", url: "https://www.perplexity.ai" },
];

function PromptCard({ text, index }) {
  const t = useT();
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    toast.success(t("teststore.copied"));
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <div className="flex items-center gap-3 rounded-lg border border-border bg-card p-4" data-testid={`prompt-card-${index}`}>
      <span className="font-mono text-sm text-brand">{String(index + 1).padStart(2, "0")}</span>
      <p className="min-w-0 flex-1 text-sm">{text}</p>
      <button onClick={copy} data-testid={`prompt-copy-${index}`}
        className="inline-flex shrink-0 items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-xs font-medium transition-colors hover:bg-secondary">
        {copied ? <Check className="h-3.5 w-3.5 text-success" /> : <Copy className="h-3.5 w-3.5" />}{t("teststore.copyPrompt")}
      </button>
    </div>
  );
}

export default function TestStore() {
  const t = useT();
  const { store, loading } = useStore();
  const [category, setCategory] = useState("");

  useEffect(() => {
    if (store) {
      api.get("/products", { params: { store_id: store.id } })
        .then(({ data }) => setCategory(data.find((p) => p.category)?.category || store.category || "products"))
        .catch(() => setCategory(store.category || "products"));
    }
  }, [store]);

  if (loading) return <div className="h-8 w-8 animate-spin rounded-full border-2 border-border border-t-brand" />;
  if (!store) {
    return (
      <div className="mx-auto max-w-lg py-16 text-center">
        <Store className="mx-auto h-10 w-10 text-muted-foreground" />
        <p className="mt-4 text-muted-foreground">{t("dashboard.home.noStore")}</p>
        <Link to="/dashboard/store" className="mt-4 inline-flex rounded-md bg-primary px-5 py-2.5 text-sm font-medium text-primary-foreground">{t("dashboard.home.noStoreCta")}</Link>
      </div>
    );
  }

  const vars = { category: category || store.category || "products", currency: store.currency === "EUR" ? "€" : store.currency === "GBP" ? "£" : "$", store: store.name };
  const prompts = [1, 2, 3, 4, 5].map((n) => t(`teststore.prompt${n}`, vars));

  return (
    <div className="max-w-3xl">
      <h1 className="flex items-center gap-2 font-display text-3xl font-extrabold tracking-tighter">
        <FlaskConical className="h-6 w-6 text-brand" />{t("teststore.title")}
      </h1>
      <p className="mt-1 text-sm text-muted-foreground">{t("teststore.subtitle")}</p>

      <div className="mt-8 space-y-2">
        {prompts.map((p, i) => <PromptCard key={i} text={p} index={i} />)}
      </div>

      <section className="mt-8">
        <h2 className="font-display text-sm font-bold uppercase tracking-wide text-muted-foreground">{t("teststore.openAssistants")}</h2>
        <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-4">
          {ASSISTANTS.map((a) => (
            <a key={a.key} href={a.url} target="_blank" rel="noreferrer" data-testid={`open-${a.key}`}
              className="flex items-center justify-between rounded-lg border border-border bg-card px-4 py-3 text-sm font-medium transition-transform hover:-translate-y-0.5">
              {a.label}<ExternalLink className="h-4 w-4 text-muted-foreground" />
            </a>
          ))}
        </div>
        <p className="mt-3 text-xs text-muted-foreground">{t("teststore.tip")}</p>
      </section>
    </div>
  );
}
